import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Wine } from 'lucide-react';
import ContainerListToolbar from '../components/toolbars/ContainerListToolbar';
import { useNavigate } from 'react-router-dom';

const ContainerList = () => {
    const navigate = useNavigate();
    const [containers, setContainers] = useState([]);
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newContainer, setNewContainer] = useState({
        cntDesc: '',
        contCapacity: '',
        cntGallons: '',
        cntDispose: '',
        cntStatus: 1
    });

// Update the useEffect in ContainerList.js
useEffect(() => {
    const fetchContainers = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/containers', {
                params: { 
                    page, 
                    limit: 50, 
                    search: search.trim() // Trim whitespace from search
                },
            });
            
            setContainers(response.data.containers);
            setTotalPages(response.data.totalPages);
            
            // Reset page if we're past the last page
            if (page > response.data.totalPages) {
                setPage(1);
            }
            
        } catch (error) {
            console.error('Error fetching containers:', error);
            setError('Failed to load containers');
        } finally {
            setLoading(false);
        }
    };

    // Add a small delay for search to prevent too many requests
    const timeoutId = setTimeout(() => {
        fetchContainers();
    }, 500);

    return () => clearTimeout(timeoutId);
}, [page, search]);

    const handleContainerRowClick = (container) => {
        setSelectedContainer(container);
        setNewContainer({
            cntDesc: container.cntDesc,
            contCapacity: container.contCapacity,
            cntGallons: container.cntGallons,
            cntDispose: container.cntDispose,
            cntStatus: container.cntStatus,
        });
        setIsEditing(true);
        setShowModal(true);
    };
    const handleSearch = (value) => {
        setSearchTerm(value);
        setSearch(value);
        setPage(1);
    };
    const clearSearch = () => {
        setSearchTerm('');
        setSearch('');
        setPage(1);
    };

    const handleDeleteContainer = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this container?')) return;

        try {
            await axios.delete(`/api/containers/${id}`);
            setContainers(prevContainers => 
                prevContainers.filter(container => container.containerID !== id)
            );
        } catch (error) {
            console.error('Error deleting container:', error);
            setError('Failed to delete container');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await axios.put(`/api/containers/${selectedContainer.containerID}`, newContainer);
            } else {
                await axios.post('/api/containers', newContainer);
            }
            
            setShowModal(false);
            setIsEditing(false);
            setSelectedContainer(null);
            setNewContainer({
                cntDesc: '',
                contCapacity: '',
                cntGallons: '',
                cntDispose: '',
                cntStatus: 1,
            });
            
            // Refresh the list
            const response = await axios.get('/api/containers', {
                params: { page: 1, limit: 50, search },
            });
            setContainers(response.data.containers);
            setTotalPages(response.data.totalPages);
            setPage(1);
        } catch (error) {
            console.error('Error saving container:', error);
            setError('Failed to save container');
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ContainerListToolbar 
                searchTerm={searchTerm}
                onAddNew={() => {
                    setIsEditing(false);
                    setSelectedContainer(null);
                    setShowModal(true);
                }}
                onSearch={handleSearch}
                onClearSearch={clearSearch}
            />

            <div className="container mt-4">
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {error}
                        <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Description</th>
                                        <th>Capacity</th>
                                        <th>Gallons</th>
                                        <th>Wine</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {containers.map((container) => (
                                        <tr 
                                            key={container.containerID}
                                            onClick={() => handleContainerRowClick(container)}
                                            className="table-row-hover"
                                            style={{ cursor: 'pointer' }}
                                        > 
                                            <td>{container.containerID}</td>
                                            <td>{container.cntDesc}</td>
                                            <td>{container.contCapacity}</td>
                                            <td>{container.cntGallons}</td>
                                            <td>
                                                <span className={`badge ${container.wineID ? 'bg-warning' : 'bg-success'} me-2`}>
                                                    {container.wineID ? 'Full' : 'Empty'}
                                                </span>
                                                {container.wineID ? 
                                                    `${container.vintage} ${container.varietal} (${container.wineID})` : 
                                                    'Empty'
                                                }
                                            </td>
                                            <td>
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            container.wineID && navigate(`/wines/${container.wineID}`);
                                                        }}
                                                        disabled={!container.wineID}
                                                    >
                                                        <Wine size={18} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteContainer(container.containerID, e);
                                                        }}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-center mt-3">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {[...Array(totalPages)].map((_, index) => (
                                        <li
                                            key={index}
                                            className={`page-item ${page === index + 1 ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setPage(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <>
                        <div className="modal-backdrop fade show"></div>
                        <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            {isEditing ? 'Edit Container' : 'Add New Container'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => {
                                                setShowModal(false);
                                                setIsEditing(false);
                                                setSelectedContainer(null);
                                                setNewContainer({
                                                    cntDesc: '',
                                                    contCapacity: '',
                                                    cntGallons: '',
                                                    cntDispose: '',
                                                    cntStatus: 1,
                                                });
                                            }}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <div className="mb-3">
                                                <div>
                                                    <button 
                                                        type="button" 
                                                        className={`btn ${selectedContainer?.wineID ? 'btn-primary' : 'btn-secondary'}`}
                                                        onClick={() => selectedContainer?.wineID && navigate(`/wines/${selectedContainer.wineID}`)}
                                                        disabled={!selectedContainer?.wineID}
                                                    >
                                                        {selectedContainer?.wineID ? 'Full' : 'Empty'}
                                                        {selectedContainer?.wineID && 
                                                            <span className="badge bg-secondary ms-1">{selectedContainer.wineID}</span>
                                                        }
                                                    </button>
                                                    {selectedContainer?.wineID && 
                                                        <span className="ms-2">
                                                            {selectedContainer.vintage} {selectedContainer.varietal}
                                                        </span>
                                                    }
                                                    </div>
                                                </div>
                                                <select
                                                    className="form-select"
                                                    value={newContainer.cntDesc}
                                                    onChange={(e) =>
                                                        setNewContainer({ ...newContainer, cntDesc: e.target.value })
                                                    }
                                                    required
                                                >
                                                    <option value="">Select Description</option>
                                                    <option value="Oak Barrel">Oak Barrel</option>
                                                    <option value="Stainless Tank">Stainless Tank</option>
                                                    <option value="Tote">Tote</option>
                                                    <option value="Keg">Keg</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Capacity</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={newContainer.contCapacity}
                                                    onChange={(e) =>
                                                        setNewContainer({ ...newContainer, contCapacity: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Gallons</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={newContainer.cntGallons}
                                                    onChange={(e) =>
                                                        setNewContainer({ ...newContainer, cntGallons: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Dispose</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newContainer.cntDispose}
                                                    onChange={(e) =>
                                                        setNewContainer({ ...newContainer, cntDispose: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <button type="submit" className="btn btn-success">
                                                {isEditing ? 'Update Container' : 'Add Container'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <style>{`
                    .table-row-hover:hover td {
                        background-color: rgba(0,0,0,.075);
                    }
                    .table-row-hover {
                        transition: background-color 0.2s ease;
                    }
                `}</style>
            </div>
        </>
    );
};

export default ContainerList;